import { useState, useEffect, useRef, useCallback } from 'react';
import type { AuthorKind, ChatMessage, QAQuestion } from '@/types';
import { getAccessToken, getAnonymousSession, getOrgOverride } from '@/api/client';

/** social-service selects this sub-protocol; the others carry credentials. */
export const CHAT_SUBPROTOCOL = 'jeket-chat';

/** social-service closes a removed guest's sockets with this code (JSS#1191): don't reconnect. */
export const PARTICIPANT_REMOVED_CLOSE_CODE = 4403;

/**
 * The chat upgrade's URL and sub-protocols (JSS#1187). A browser WebSocket
 * cannot set headers, so the signed-in user's bearer rides `bearer.<token>`
 * (the BFF verifies it and strips it), the org-switcher pick the `org` query
 * parameter, and an admitted guest's token `participant.<token>` (verified
 * by social-service). A private event's chat refuses the upgrade without
 * them.
 *
 * Identity is the server's (JSS#1191): no user id or name is sent. The tab's
 * anonymous session (`anonymous.<token>`) is always offered: social-service
 * uses it only for an anonymous caller, whose chat and Q&A then share one
 * anonymous author, and refuses an anonymous send without it. `displayName`
 * is only an anonymous viewer's asked-for name, used where the event allows
 * it and marked unverified.
 */
export function chatSocketTarget(opts: {
  host: string;
  secure: boolean;
  eventId: string;
  displayName?: string;
  participantToken?: string;
}): { url: string; protocols: string[] } {
  const params = new URLSearchParams({ eventId: opts.eventId });
  if (opts.displayName) params.set('name', opts.displayName);
  const org = getOrgOverride();
  if (org) params.set('org', org);
  const protocols = [CHAT_SUBPROTOCOL];
  const token = getAccessToken();
  if (token) protocols.push(`bearer.${token}`);
  protocols.push(`anonymous.${getAnonymousSession()}`);
  if (opts.participantToken) protocols.push(`participant.${opts.participantToken}`);
  return { url: `${opts.secure ? 'wss:' : 'ws:'}//${opts.host}/api/ws/chat?${params.toString()}`, protocols };
}

/**
 * Who social-service says this connection is (its `identity` message).
 * `userId` is opaque per event unless the caller is the event's member;
 * `canWrite` is false for an anonymous socket without a session.
 */
export interface ChatIdentity {
  userId: string | null;
  username: string;
  verified: boolean;
  kind: AuthorKind;
  canWrite: boolean;
}

/** A Q&A change pushed on the chat socket: a question (as this caller may see it), or one that left its view. */
export type QaChange = { type: 'qa_update'; question: QAQuestion } | { type: 'qa_removed'; id: string };

interface UseEventChatOptions {
  eventId: string;
  /** A signed-out viewer's asked-for name; shown unverified, and only where the event allows it. */
  displayName?: string;
  enabled?: boolean;
  /** An admitted guest's X-Participant-Token, for a private event's chat. */
  participantToken?: string;
  /** Called on a Q&A change (JSS#1191); refetch or patch the question list. */
  onQaChange?: (change: QaChange) => void;
}

interface UseEventChatReturn {
  messages: ChatMessage[];
  sendMessage: (content: string, replyToId?: string) => void;
  isConnected: boolean;
  viewerCount: number;
  identity: ChatIdentity | null;
  /** True once social-service closed the socket because the guest was removed. */
  removed: boolean;
}

export function useEventChat({
  eventId,
  displayName,
  enabled = true,
  participantToken,
  onQaChange,
}: UseEventChatOptions): UseEventChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [identity, setIdentity] = useState<ChatIdentity | null>(null);
  const [removed, setRemoved] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const reconnectAttemptRef = useRef(0);
  const onQaChangeRef = useRef(onQaChange);
  onQaChangeRef.current = onQaChange;

  const connect = useCallback(() => {
    if (!enabled || !eventId) return;

    const { url, protocols } = chatSocketTarget({
      host: window.location.host,
      secure: window.location.protocol === 'https:',
      eventId,
      displayName,
      participantToken,
    });

    const ws = new WebSocket(url, protocols);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      reconnectAttemptRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'message') {
          setMessages((prev) => [...prev, msg.data]);
        } else if (msg.type === 'viewer_count') {
          setViewerCount(msg.data.count);
        } else if (msg.type === 'system') {
          setMessages((prev) => [...prev, msg.data]);
        } else if (msg.type === 'identity') {
          setIdentity(msg.data as ChatIdentity);
        } else if (msg.type === 'qa_update') {
          onQaChangeRef.current?.({ type: 'qa_update', question: msg.data as QAQuestion });
        } else if (msg.type === 'qa_removed') {
          onQaChangeRef.current?.({ type: 'qa_removed', id: String(msg.data?.id) });
        }
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      wsRef.current = null;

      // A removed guest is not let back in: reconnecting would only be refused.
      if (event.code === PARTICIPANT_REMOVED_CLOSE_CODE) {
        setRemoved(true);
        return;
      }

      // Reconnect with exponential backoff
      if (enabled) {
        const delay = Math.min(1000 * 2 ** reconnectAttemptRef.current, 30000);
        reconnectAttemptRef.current++;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [eventId, displayName, enabled, participantToken]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const sendMessage = useCallback((content: string, replyToId?: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content, replyToId }));
    }
  }, []);

  return { messages, sendMessage, isConnected, viewerCount, identity, removed };
}
